import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useAppDialog } from '@/hooks/useAppDialog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, MessageSquare, Link as LinkIcon } from 'lucide-react';

const STAGES = ['APPLIED','SCREENING','INTERVIEW','OFFER','ONBOARDING','ONBOARDING_REVIEW','APPROVED','CONVERTED_TO_STAFF','HIRED','REJECTED'];
const STAGE_COLORS = {APPLIED:'border-blue-200',SCREENING:'border-amber-200',INTERVIEW:'border-purple-200',OFFER:'border-teal-200',ONBOARDING:'border-orange-200',ONBOARDING_REVIEW:'border-pink-200',APPROVED:'border-cyan-200',CONVERTED_TO_STAFF:'border-emerald-200',HIRED:'border-emerald-200',REJECTED:'border-red-200'};

export default function RecruitmentPipelinePage() {
  const { dangerConfirm } = useAppDialog();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createJobOpen, setCreateJobOpen] = useState(false);
  const [onboardingDialog, setOnboardingDialog] = useState(null);
  const [onboardingLink, setOnboardingLink] = useState('');
  const [sendingMethod, setSendingMethod] = useState('');
  const [jobForm, setJobForm] = useState({ title: '', department: '', employment_type: 'FULL_TIME', location: '', description: '', salary_range: '' });
  const [form, setForm] = useState({ first_name:'', last_name:'', email:'', phone:'', position_id:'', source:'MANUAL', notes:'', rating:3 });
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const [cR, jR] = await Promise.allSettled([
        api.get('/candidates?pageSize=100'),
        api.get('/jobs?pageSize=100'),
      ]);
      setCandidates(cR.status === 'fulfilled' ? (cR.value.data.items || cR.value.data.data || []) : []);
      setJobs(jR.status === 'fulfilled' ? (jR.value.data.items || jR.value.data.data || []) : []);
    } catch (e) {}
  };

  useEffect(() => { refresh(); }, []);

  const moveStage = async (id, stage) => {
    try {
      await api.post(`/candidates/${id}/move-stage`, { stage });
      refresh();
      toast.success(`Moved to ${stage}`);
    } catch (e) { toast.error('Move failed'); }
  };

  const createCandidate = async (e) => {
    e.preventDefault();
    if (!form.position_id) { toast.error('Please select a position'); return; }
    try {
      await api.post('/candidates', form);
      toast.success('Candidate added');
      setCreateOpen(false);
      setForm({ first_name:'', last_name:'', email:'', phone:'', position_id:'', source:'MANUAL', notes:'', rating:3 });
      refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed to create candidate'); }
  };

  const createJob = async (e) => {
    e.preventDefault();
    if (!jobForm.title) { toast.error('Position title is required'); return; }
    try {
      await api.post('/jobs', { ...jobForm, branch_id: 'MAIN' });
      toast.success('Position created');
      setCreateJobOpen(false);
      setJobForm({ title: '', department: '', employment_type: 'FULL_TIME', location: '', description: '', salary_range: '' });
      refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed'); }
  };

  const sendOnboarding = async (candidateId, method) => {
    setSendingMethod(method);
    try {
      const res = await api.post('/recruitment/onboarding/send', { candidate_id: candidateId, send_method: method });
      const url = res.data.data.onboarding_url;
      setOnboardingLink(url);
      if (method === 'COPY') {
        navigator.clipboard.writeText(url).then(() => toast.success('Link copied to clipboard!')).catch(() => toast.success('Link generated — copy from below'));
      } else {
        toast.success(`Onboarding link sent via ${method}!`);
      }
      refresh();
    } catch (err) { toast.error(err.response?.data?.error?.message || 'Failed'); }
    setSendingMethod('');
  };

  const openPositions = jobs.filter(j => j.status === 'OPEN');

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight">Recruitment Pipeline</h1><p className="text-muted-foreground mt-1">{openPositions.length} open position(s) · {candidates.length} candidate(s)</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCreateJobOpen(true)} data-testid="add-position-button">+ New Position</Button>
          <Button onClick={() => { if (openPositions.length === 0) { toast.error('Create a position first'); return; } setCreateOpen(true); }} data-testid="add-candidate-button">+ New Candidate</Button>
        </div>
      </div>

      {/* Open Positions Bar */}
      {openPositions.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {openPositions.map(j => (
            <Card key={j.id} className="border-green-200">
              <CardContent className="py-2 px-3 flex items-center gap-2">
                <StatusBadge status="OPEN" />
                <span className="text-sm font-medium">{j.title}</span>
                <span className="text-xs text-muted-foreground">{j.department || ''} · {j.employment_type?.replace('_', ' ')}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {openPositions.length === 0 && (
        <Card className="border-dashed border-2 border-amber-300 bg-amber-50">
          <CardContent className="py-4 text-center">
            <p className="text-amber-700 font-medium">No open positions yet</p>
            <p className="text-amber-600 text-sm mt-1">Create a position first, then you can add candidates</p>
            <Button className="mt-3" variant="outline" onClick={() => setCreateJobOpen(true)}>+ Create First Position</Button>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-6 gap-3 min-h-[500px]">
        {STAGES.map(stage => {
          const stageCands = candidates.filter(c => c.stage === stage);
          return (
            <div key={stage} className={`border-2 rounded-lg p-2 ${STAGE_COLORS[stage] || 'border-gray-200'} bg-muted/20`}>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs font-bold uppercase">{stage}</span>
                <span className="text-xs text-muted-foreground">{stageCands.length}</span>
              </div>
              <div className="space-y-2">
                {stageCands.map(c => (
                  <Card key={c.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate(`/hr/candidates/${c.id}`)}>
                    <CardContent className="p-2">
                      <p className="text-sm font-medium">{c.first_name} {c.last_name}</p>
                      <p className="text-[10px] text-muted-foreground">{c.email}</p>
                      {c.rating && <p className="text-[10px]">{'\u2605'.repeat(c.rating)}{'\u2606'.repeat(5-c.rating)}</p>}
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {stage !== 'HIRED' && stage !== 'REJECTED' && stage !== 'OFFER' && stage !== 'ONBOARDING' && stage !== 'APPROVED' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); const next = STAGES[STAGES.indexOf(stage)+1]; if(next) moveStage(c.id, next); }}>
                            Move to {STAGES[STAGES.indexOf(stage)+1] || 'Next'}
                          </Button>
                        )}
                        {stage === 'OFFER' && (
                          <Button size="sm" variant="default" className="h-7 text-xs px-2 bg-orange-500 hover:bg-orange-600" onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            setOnboardingDialog(c); setOnboardingLink('');
                          }}>
                            Send Onboarding
                          </Button>
                        )}
                        {stage === 'ONBOARDING' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={async (e) => {
                            e.preventDefault(); e.stopPropagation();
                            try { await api.post(`/recruitment/onboarding/approve/${c.id}`); toast.success('Onboarding approved!'); refresh(); }
                            catch(err) { toast.error(err.response?.data?.error?.message || 'Failed'); }
                          }}>
                            Approve Onboarding
                          </Button>
                        )}
                        {stage === 'ONBOARDING_REVIEW' && (
                          <>
                            <Button size="sm" variant="default" className="h-7 text-xs px-2 bg-green-600 hover:bg-green-700" onClick={async (e) => {
                              e.preventDefault(); e.stopPropagation();
                              try { await api.post(`/recruitment/onboarding/review/${c.id}`, { status: 'APPROVED' }); toast.success('Onboarding approved!'); refresh(); }
                              catch(err) { toast.error(err.response?.data?.error?.message || 'Failed'); }
                            }}>Approve</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-amber-600" onClick={async (e) => {
                              e.preventDefault(); e.stopPropagation();
                              const result = await dangerConfirm({
                                title: 'Request resubmission',
                                description: 'Tell the candidate what needs to be fixed. They will be able to update and resubmit.',
                                confirmLabel: 'Send back',
                                cancelLabel: 'Cancel',
                                reasonRequired: true,
                                variant: 'warning',
                                testId: 'recruitment-resubmit-confirm',
                              });
                              if (!result || !result.reason) return;
                              try { await api.post(`/recruitment/onboarding/review/${c.id}`, { status: 'ACTION_REQUIRED', review_notes: result.reason }); toast.success('Resubmission requested'); refresh(); }
                              catch(err) { toast.error(err.response?.data?.error?.message || 'Failed'); }
                            }}>Resubmit</Button>
                          </>
                        )}
                        {stage === 'APPROVED' && (
                          <Button size="sm" variant="default" className="h-7 text-xs px-2" onClick={async (e) => {
                            e.preventDefault(); e.stopPropagation();
                            try {
                              const elig = await api.get(`/candidates/${c.id}/conversion-eligibility`);
                              if (!elig.data.data.eligible) {
                                toast.error('Not eligible: ' + elig.data.data.reasons.join(', '));
                                return;
                              }
                              await api.post(`/candidates/${c.id}/hire`);
                              toast.success('Candidate converted to staff!'); refresh();
                            } catch(err) { toast.error(err.response?.data?.error?.message || err.response?.data?.detail?.error?.message || 'Conversion failed'); }
                          }}>
                            Convert to Staff
                          </Button>
                        )}
                        {stage !== 'HIRED' && stage !== 'REJECTED' && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-red-500" onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveStage(c.id, 'REJECTED'); }}>
                            Reject
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Candidate Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Candidate</DialogTitle>
            <DialogDescription>Enter candidate details</DialogDescription>
          </DialogHeader>
          <form onSubmit={createCandidate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>First Name</Label><Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required data-testid="candidate-firstname" /></div>
              <div className="space-y-2"><Label>Last Name</Label><Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required data-testid="candidate-lastname" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="candidate-email" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Position</Label>
                <Select value={form.position_id} onValueChange={(v) => setForm({...form, position_id: v})}>
                  <SelectTrigger data-testid="candidate-position"><SelectValue placeholder="Select position" /></SelectTrigger>
                  <SelectContent>
                    {jobs.filter(j => j.status === 'OPEN').map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['WEBSITE','SEEK','REFERRAL','MANUAL','OTHER'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
            <Button type="submit" className="w-full" data-testid="candidate-submit">Add Candidate</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Position Dialog */}
      <Dialog open={createJobOpen} onOpenChange={setCreateJobOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Position</DialogTitle>
            <DialogDescription>Add a job position for recruitment</DialogDescription>
          </DialogHeader>
          <form onSubmit={createJob} className="space-y-4">
            <div className="space-y-2"><Label>Position Title *</Label><Input value={jobForm.title} onChange={e => setJobForm({...jobForm, title: e.target.value})} placeholder="e.g. Support Worker" required data-testid="job-title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Department</Label><Input value={jobForm.department} onChange={e => setJobForm({...jobForm, department: e.target.value})} placeholder="e.g. Clinical" /></div>
              <div className="space-y-2"><Label>Employment Type</Label>
                <Select value={jobForm.employment_type} onValueChange={v => setJobForm({...jobForm, employment_type: v})}>
                  <SelectTrigger data-testid="job-employment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['FULL_TIME','PART_TIME','CASUAL','CONTRACT'].map(t => <SelectItem key={t} value={t}>{t.replace('_',' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Location</Label><Input value={jobForm.location} onChange={e => setJobForm({...jobForm, location: e.target.value})} placeholder="e.g. Sydney" /></div>
              <div className="space-y-2"><Label>Salary Range</Label><Input value={jobForm.salary_range} onChange={e => setJobForm({...jobForm, salary_range: e.target.value})} placeholder="e.g. $55k-$65k" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={jobForm.description} onChange={e => setJobForm({...jobForm, description: e.target.value})} placeholder="Brief job description" /></div>
            <Button type="submit" className="w-full" data-testid="job-submit">Create Position</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Onboarding Link Dialog */}
      <Dialog open={!!onboardingDialog} onOpenChange={() => setOnboardingDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Onboarding Link</DialogTitle>
            <DialogDescription>
              Send onboarding link to {onboardingDialog?.first_name} {onboardingDialog?.last_name}
              {onboardingDialog?.email && <span className="block text-xs mt-1">{onboardingDialog.email}</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" disabled={!!sendingMethod} onClick={() => sendOnboarding(onboardingDialog.id, 'EMAIL')} data-testid="send-email-btn">
                <Mail className="h-5 w-5" />
                <span className="text-xs">{sendingMethod === 'EMAIL' ? 'Sending...' : 'Send Email'}</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" disabled={!!sendingMethod} onClick={() => sendOnboarding(onboardingDialog.id, 'SMS')} data-testid="send-sms-btn">
                <MessageSquare className="h-5 w-5" />
                <span className="text-xs">{sendingMethod === 'SMS' ? 'Sending...' : 'Send SMS'}</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-primary text-primary" disabled={!!sendingMethod} onClick={() => sendOnboarding(onboardingDialog.id, 'COPY')} data-testid="copy-link-btn">
                <LinkIcon className="h-5 w-5" />
                <span className="text-xs">{sendingMethod === 'COPY' ? 'Copying...' : 'Copy Link'}</span>
              </Button>
            </div>

            {onboardingLink && (
              <div className="space-y-2 pt-2 border-t">
                <p className="text-xs font-medium text-muted-foreground">Onboarding Link:</p>
                <div className="flex gap-2">
                  <Input value={onboardingLink} readOnly className="text-xs font-mono" data-testid="onboarding-link-input" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(onboardingLink); toast.success('Copied!'); }} data-testid="copy-btn">Copy</Button>
                </div>
                <p className="text-[10px] text-muted-foreground">Share this link with the candidate. Valid for 7 days.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
